import { useState, type ReactNode } from 'react';
import { SizePicker } from './SizePicker';
import { PrintSettings } from './PrintSettings';
import { DriftAdjuster } from './DriftAdjuster';
import type { AppState } from '../hooks/useAppState';

interface Props {
  app: AppState;
  insideEnabled?: boolean;
}

interface CollapsibleSectionProps {
  title: ReactNode;
  intro?: ReactNode;
  defaultCollapsed?: boolean;
  className?: string;
  children: ReactNode;
}

/** Header + intro + body, with a chevron on the header that hides everything
 *  below when clicked. Each section in studio-left wraps its content here. */
function CollapsibleSection({
  title,
  intro,
  defaultCollapsed = false,
  className = '',
  children,
}: CollapsibleSectionProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className={`settings-group${collapsed ? ' collapsed' : ''} ${className}`}>
      <button
        type="button"
        className="settings-group-toggle"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <h3 className="settings-group-title">{title}</h3>
        <span className="settings-group-chev" aria-hidden>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 6 8 10 12 6" />
          </svg>
        </span>
      </button>
      {!collapsed && (
        <div className="settings-group-body">
          {intro && <p className="settings-group-intro">{intro}</p>}
          {children}
        </div>
      )}
    </div>
  );
}

export function SettingsGroupCardPaper({ app }: Props) {
  return (
    <CollapsibleSection
      title="Card & paper"
      intro={
        <>
          Pick the card orientation, folded card size, and paper. Portrait gives a tall
          card with a vertical fold (default). Landscape gives a wide card with a horizontal
          fold; paper auto-flips to match. Paper isn't fixed — anything bigger than the
          spread works at 100% / Actual size.
        </>
      }
    >
      <SizePicker
        cardSize={app.cardSize}
        paperSize={app.paperSize}
        orientation={app.orientation}
        onCardSizeChange={app.setCardSize}
        onPaperSizeChange={app.setPaperSize}
        onOrientationChange={app.setOrientation}
      />
      {app.fitError && <p className="global-error">{app.fitError}</p>}
    </CollapsibleSection>
  );
}

export function SettingsGroupPrint({ app, insideEnabled = true }: Props) {
  return (
    <CollapsibleSection
      title="Print settings"
      intro={
        <>
          Choose which guide marks the PDF should include (corner cut marks, fold line,
          card border).
          {insideEnabled && (
            <>
              {' '}For two-sided cards, also pick how your printer flips paper —
              <strong> short-edge flip</strong> is the home-printer default,
              <strong> long-edge flip</strong> rotates the inside 180°, and
              <strong> two-sheet</strong> prints each side separately to glue back-to-back.
            </>
          )}
        </>
      }
    >
      <PrintSettings
        marks={app.marks}
        printer={app.printer}
        preserveResolution={app.preserveResolution}
        onMarksChange={app.setMarks}
        onPrinterChange={app.setPrinter}
        onPreserveResolutionChange={app.setPreserveResolution}
        insideEnabled={insideEnabled}
      />
    </CollapsibleSection>
  );
}

export function SettingsGroupCalibration({ app }: Props) {
  return (
    <CollapsibleSection
      className="settings-group-calibration"
      defaultCollapsed
      title={
        <>
          Calibration <span className="optional-pill">optional</span>
        </>
      }
      intro={
        <>
          Most home printers drift 1–3 mm between the front and back of a duplex print —
          enough to nudge a card off the fold line. Print the alignment test first, hold the
          two pages up to a window, read the offset off the labeled millimetre rulers, then
          enter the values here. The inside page will be pre-shifted so the printer's drift
          cancels out.
        </>
      }
    >
      <div className="calibration-test">
        <div className="calibration-test-head">
          <span className="calibration-test-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
          </span>
          <div className="calibration-test-text">
            <h4 className="calibration-test-title">Printer alignment test</h4>
            <p className="calibration-test-desc">
              A pre-built grid PDF for measuring your printer's duplex drift. Print
              it once, hold both pages up to a window, read the offset off the
              labeled millimetre rulers.
            </p>
          </div>
        </div>
        <a
          href="/printer-alignment-test.pdf"
          download="printer-alignment-test.pdf"
          className="calibration-test-cta"
          rel="noopener"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span className="calibration-test-cta-label">Download alignment-test PDF</span>
          <span className="calibration-test-cta-meta">PDF · 1.1 MB</span>
        </a>
        <p className="calibration-test-hint">
          Print at <strong>100% / Actual size</strong>, with duplex on, using the
          same flip mode you'll use for the card.
        </p>
      </div>
      <DriftAdjuster drift={app.drift} onChange={app.setDrift} />
    </CollapsibleSection>
  );
}

export function SettingsHub({ app, insideEnabled = true }: Props) {
  return (
    <div className="settings-hub">
      <SettingsGroupCardPaper app={app} />
      <SettingsGroupPrint app={app} insideEnabled={insideEnabled} />
      {insideEnabled && <SettingsGroupCalibration app={app} />}
    </div>
  );
}
